import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, isAdmin } from "../lib/roles";
import { Empty, Field, Loading, PageHeader, Panel, SevBadge } from "../components/common";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

export default function AlertDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [alert, setAlert] = useState(undefined);
  const [edit, setEdit] = useState(false);
  const [msg, setMsg] = useState("");
  const [sev, setSev] = useState("attenzione");

  useEffect(() => {
    api("/alerts").then((d) => setAlert([...(d.alerts || []), ...(d.sent || [])].find((a) => a.id === id) || null)).catch(() => setAlert(null));
  }, [id]);
  if (alert === undefined) return <Loading />;
  if (!alert) return <Empty title="Alert non trovato." />;
  const canEdit = isAdmin(user) || alert.from_id === user?.id || alert.from_user_id === user?.id;

  const save = async () => {
    if (!msg.trim()) return toast.error("Il messaggio non può essere vuoto");
    try { await api(`/alerts/${alert.id}`, { method: "PUT", body: { messaggio: msg, severita: sev } }); setAlert({ ...alert, messaggio: msg, severita: sev }); setEdit(false); toast.success("Alert aggiornato"); }
    catch (e) { toast.error(e.message); }
  };
  const del = async () => {
    if (!window.confirm("Vuoi eliminare questo alert?")) return;
    try { await api(`/alerts/${alert.id}`, { method: "DELETE" }); toast.success("Alert eliminato"); nav("/avvisi"); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="max-w-2xl">
      <PageHeader subtitle="Dettaglio Alert" title={alert.categoria} />
      <Panel className="space-y-4" data-testid="alert-detail">
        <SevBadge sev={alert.severita} />
        {alert.messaggio && <p className="whitespace-pre-wrap">{alert.messaggio}</p>}
        {alert.immagine && <a href={alert.immagine} target="_blank" rel="noreferrer"><img src={alert.immagine} alt="alert" className="rounded-xl max-h-96 object-cover" /></a>}
        {alert.lat != null && alert.lng != null ? (
          <a href={`https://www.google.com/maps?q=${alert.lat},${alert.lng}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[var(--teal)] font-medium" data-testid="alert-map-link"><MapPin className="w-4 h-4" />Apri posizione in Google Maps</a>
        ) : <p className="text-sm text-slate-500">Posizione non disponibile per questo alert.</p>}
        <p className="text-xs text-slate-400">Inviato da {alert.from_name} · {fmtDate(alert.created_at, true)}</p>
        {canEdit && !edit && (
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="rounded-full" onClick={() => { setMsg(alert.messaggio || ""); setSev(alert.severita); setEdit(true); }} data-testid="alert-edit-button">Modifica</Button>
            <Button variant="ghost" className="rounded-full text-[var(--red)]" onClick={del} data-testid="alert-detail-delete-button">Elimina</Button>
          </div>
        )}
        {edit && (
          <div className="space-y-4 border-t pt-4">
            <div className="flex gap-2">{["attenzione", "critico"].map((v) => <button key={v} onClick={() => setSev(v)} className={`px-3 py-1.5 rounded-full text-sm border ${sev === v ? "bg-[var(--navy)] text-white" : ""}`} data-testid={`alert-edit-sev-${v}`}>{v}</button>)}</div>
            <Field label="Messaggio"><Textarea value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="alert-edit-message-input" /></Field>
            <Button onClick={save} className="rounded-full bg-[var(--teal)]" data-testid="alert-edit-save-button">Salva modifiche</Button>
          </div>
        )}
      </Panel>
    </div>
  );
}
